import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { api, formatCurrency } from '@/lib/api';
import { useAuth, isAgent } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, FileDown, Package, ShoppingCart, FileText, Send, Sparkles, MapPin, X as XIcon } from 'lucide-react';
import { QuoteBusinessPlanDialog } from '@/components/QuoteBusinessPlanDialog';
import { getZoneForProvince, ZONE_LABELS, ZONE_REGIONS } from '@/lib/geoZones';

export default function Listino() {
  const { user } = useAuth();
  const canViewPrices = !(isAgent(user) && user?.agent_permissions?.can_view_prices === false);
  const [products, setProducts] = useState([]);
  const [options, setOptions] = useState({ product_categories: [] });
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPhase, setFilterPhase] = useState('all'); // 'all' | 'monofase' | 'trifase'
  const [filterBrand, setFilterBrand] = useState('all');
  const [powerMin, setPowerMin] = useState('');
  const [powerMax, setPowerMax] = useState('');
  const [kwhMin, setKwhMin] = useState('');
  const [kwhMax, setKwhMax] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [cart, setCart] = useState([]);
  const [vatRate, setVatRate] = useState(10);
  const [customer, setCustomer] = useState({ customer_type: 'privato', first_name: '', last_name: '', company_name: '', fiscal_code: '', vat_number: '', pec: '', sdi_code: '', email: '', phone: '', residence_address: '', residence_city: '', residence_cap: '', residence_province: '', billing_address: '', billing_city: '', billing_cap: '', billing_province: '' });
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [validityDays, setValidityDays] = useState(30);
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [bpDialogOpen, setBpDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [opts, comps] = await Promise.all([api.getOptions(), api.listCompanies()]);
        setOptions(opts); setCompanies(comps);
        // Default company: agent/partner -> own; admin -> first company
        const cid = user?.company_id || (comps[0]?.id || '');
        if (cid) setCompanyId(cid);
      } catch { toast.error('Errore caricamento'); }
    })();
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!companyId) { setProducts([]); return; }
    api.listProducts({ company_id: companyId, active: true }).then(setProducts).catch(() => setProducts([]));
  }, [companyId]);

  const catMap = useMemo(() => Object.fromEntries((options.product_categories || []).map(c => [c.key, c.label])), [options]);

  const parseNumericPower = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim().replace(',', '.');
    const m = s.match(/([\d.]+)/);
    if (!m) return null;
    let n = parseFloat(m[1]);
    if (Number.isNaN(n)) return null;
    if (/wp\b|watt/i.test(s) && !/kwp?\b/i.test(s)) n = n / 1000;
    return n;
  };

  const brandOptions = useMemo(() => {
    const set = new Set();
    products.forEach(p => { const b = (p.brand || '').trim(); if (b) set.add(b); });
    return [...set].sort((a, b) => a.localeCompare(b, 'it'));
  }, [products]);

  // Zone commerciale rilevata dall'indirizzo del cliente
  const detectedZone = useMemo(() => getZoneForProvince(customer.residence_province), [customer.residence_province]);
  const [zoneFilter, setZoneFilter] = useState('auto'); // 'auto' | 'all' | 'nord' | 'centro' | 'sud'
  const activeZone = zoneFilter === 'auto' ? detectedZone : (zoneFilter === 'all' ? null : zoneFilter);

  // Verifica se il partner ha almeno un prodotto con sales_zone valorizzata (listino zonizzato)
  const hasZonedListino = useMemo(() => products.some(p => p.sales_zone), [products]);

  // Il partner ha almeno un prodotto con phase_type valorizzato (listino fotovoltaico strutturato)
  const hasPhaseFilter = useMemo(() => products.some(p => p.phase_type), [products]);

  const filtered = products.filter(p => {
    if (filterCategory !== 'all' && p.category !== filterCategory) return false;
    if (filterBrand !== 'all' && (p.brand || '').trim() !== filterBrand) return false;
    // Filtro zona
    if (hasZonedListino && activeZone && p.sales_zone && p.sales_zone !== activeZone) return false;
    // Filtro fase (monofase/trifase)
    if (filterPhase !== 'all' && p.phase_type && p.phase_type !== filterPhase) return false;
    if (filterPhase !== 'all' && !p.phase_type && hasPhaseFilter) {
      // Se stiamo filtrando per fase e il prodotto non ha phase_type, escludiamolo (es. accessori)
      // MA teniamo le voci personalizzabili sempre visibili come alternativa
      if (!p.is_custom) return false;
    }
    const pw = parseNumericPower(p.power);
    if (powerMin !== '') {
      const min = parseFloat(String(powerMin).replace(',', '.'));
      if (!Number.isNaN(min) && (pw == null || pw < min)) return false;
    }
    if (powerMax !== '') {
      const max = parseFloat(String(powerMax).replace(',', '.'));
      if (!Number.isNaN(max) && (pw == null || pw > max)) return false;
    }
    const kwh = Number(p.battery_kwh) || 0;
    if (kwhMin !== '') {
      const min = parseFloat(String(kwhMin).replace(',', '.'));
      if (!Number.isNaN(min) && kwh < min) return false;
    }
    if (kwhMax !== '') {
      const max = parseFloat(String(kwhMax).replace(',', '.'));
      if (!Number.isNaN(max) && kwh > max) return false;
    }
    const price = Number(p.unit_price) || 0;
    if (priceMin !== '') {
      const min = parseFloat(String(priceMin).replace(',', '.'));
      if (!Number.isNaN(min) && price < min) return false;
    }
    if (priceMax !== '') {
      const max = parseFloat(String(priceMax).replace(',', '.'));
      if (!Number.isNaN(max) && price > max) return false;
    }
    if (search) {
      const t = search.toLowerCase();
      if (![p.brand, p.model, p.description].some(s => (s || '').toLowerCase().includes(t))) return false;
    }
    return true;
  });

  const activeFiltersCount =
    (filterCategory !== 'all' ? 1 : 0) +
    (filterBrand !== 'all' ? 1 : 0) +
    (filterPhase !== 'all' ? 1 : 0) +
    (powerMin !== '' ? 1 : 0) +
    (powerMax !== '' ? 1 : 0) +
    (kwhMin !== '' ? 1 : 0) +
    (kwhMax !== '' ? 1 : 0) +
    (priceMin !== '' ? 1 : 0) +
    (priceMax !== '' ? 1 : 0) +
    (search ? 1 : 0);

  const resetFilters = () => {
    setFilterCategory('all'); setFilterBrand('all'); setFilterPhase('all');
    setPowerMin(''); setPowerMax(''); setKwhMin(''); setKwhMax('');
    setPriceMin(''); setPriceMax(''); setSearch('');
    setPage(1);
  };

  // Reset pagina quando cambiano filtri o dataset
  useEffect(() => { setPage(1); }, [
    filterCategory, filterBrand, filterPhase, powerMin, powerMax,
    kwhMin, kwhMax, priceMin, priceMax, search, zoneFilter, products.length,
  ]);

  // Paginazione (max 20 per pagina)
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedProducts = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const totals = useMemo(() => {
    const subtotal = cart.reduce((acc, p) => acc + (Number(p.quantity) || 0) * (Number(p.unit_price) || 0), 0);
    const vat = subtotal * (Number(vatRate) || 0) / 100;
    return { subtotal, vat, total: subtotal + vat };
  }, [cart, vatRate]);

  const addToCart = (p) => {
    if (!hasMinCustomerData) { toast.error('Compila prima i dati minimi del cliente (Nome, Cognome, Città, Provincia)'); return; }
    setCart(prev => {
      // Voci is_custom devono essere sempre ADD (non merge) per essere personalizzate in modo indipendente
      if (!p.is_custom) {
        const idx = prev.findIndex(it => it.product_id === p.id);
        if (idx >= 0) {
          const next = [...prev]; next[idx] = { ...next[idx], quantity: (next[idx].quantity || 0) + 1 }; return next;
        }
      }
      return [...prev, {
        product_id: p.id, category: p.category, brand: p.brand, model: p.model,
        description: p.description, power: p.power, quantity: 1, unit_price: p.unit_price,
        is_custom: !!p.is_custom,
      }];
    });
    toast.success(`Aggiunto: ${p.brand || p.model || 'Voce'}`);
  };
  const addFreeItem = () => {
    if (!hasMinCustomerData) { toast.error('Compila prima i dati minimi del cliente (Nome, Cognome, Città, Provincia)'); return; }
    setCart(prev => [...prev, {
      product_id: null, category: 'accessorio', brand: '', model: 'Voce libera',
      description: '', power: '', quantity: 1, unit_price: 0, is_custom: true,
    }]);
  };
  const removeFromCart = (i) => setCart(prev => prev.filter((_, j) => j !== i));
  const updateCartItem = (i, key, value) => setCart(prev => prev.map((it, j) => j === i ? { ...it, [key]: value } : it));

  // Dati minimi obbligatori per creare un preventivo (nome/cognome o rag. sociale, città, provincia)
  const requiredCustomerFields = useMemo(() => {
    const isPriv = customer.customer_type === 'privato';
    const missing = [];
    if (isPriv) {
      if (!customer.first_name?.trim()) missing.push('Nome');
      if (!customer.last_name?.trim()) missing.push('Cognome');
    } else {
      if (!customer.company_name?.trim()) missing.push('Ragione sociale');
    }
    if (!customer.residence_city?.trim()) missing.push('Città');
    if (!customer.residence_province?.trim()) missing.push('Provincia');
    return missing;
  }, [customer]);
  const hasMinCustomerData = requiredCustomerFields.length === 0;

  const handleGenerate = async () => {
    if (cart.length === 0) { toast.error('Aggiungi almeno un prodotto al preventivo'); return; }
    if (!companyId) { toast.error('Seleziona l\'azienda'); return; }
    setBpDialogOpen(true);
  };

  const buildPayload = () => ({
    company_id: companyId,
    customer,
    products: cart,
    vat_rate: vatRate,
    validity_days: validityDays,
    notes,
    title: 'Soluzione di Efficientamento Energetico',
    date: new Date().toISOString().slice(0, 10),
  });

  return (
    <div>
      <PageHeader
        title="Listino prodotti"
        description="Sfoglia il catalogo, aggiungi articoli al preventivo e genera l'offerta commerciale per il cliente"
        actions={
          user?.role === 'admin' && companies.length > 1 && (
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-full sm:w-[240px]" data-testid="listino-company-select"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
              <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col space-y-4">
          <Card className="order-2" data-testid="listino-products-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="size-4 text-primary" />2. Selezione Prodotti
                {!hasMinCustomerData && <Badge variant="destructive" className="ml-2 text-[10px]">Dati cliente incompleti</Badge>}
              </CardTitle>
              {!hasMinCustomerData && (
                <p className="text-xs text-destructive" data-testid="listino-required-warning">
                  ⚠ Compila prima i dati minimi del cliente per poter aggiungere prodotti: <strong>{requiredCustomerFields.join(', ')}</strong>
                </p>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {/* Banner zona geografica rilevata (solo se listino zonizzato) */}
              {hasZonedListino && (
                <div className={`px-4 py-2.5 border-b flex flex-wrap items-center gap-3 text-xs ${activeZone ? 'bg-primary/5 border-b-primary/20' : 'bg-amber-500/5 border-b-amber-500/30'}`} data-testid="listino-zone-banner">
                  <MapPin className={`size-3.5 ${activeZone ? 'text-primary' : 'text-amber-600 dark:text-amber-400'}`} />
                  {activeZone ? (
                    <span>Listino <strong>{ZONE_LABELS[activeZone]}</strong> attivo{detectedZone && zoneFilter === 'auto' ? <> — rilevato da provincia <strong>{customer.residence_province}</strong></> : null} <span className="text-muted-foreground">({ZONE_REGIONS[activeZone]})</span></span>
                  ) : zoneFilter === 'all' ? (
                    <span className="text-muted-foreground">Visualizzazione <strong>tutte le zone</strong> — ricorda di scegliere la zona corretta prima di confermare</span>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-300">Compila la <strong>provincia del cliente</strong> per attivare automaticamente il listino della zona corretta</span>
                  )}
                  <div className="flex-1" />
                  <Select value={zoneFilter} onValueChange={setZoneFilter}>
                    <SelectTrigger className="h-7 w-[180px]" data-testid="listino-zone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto{detectedZone ? ` (${ZONE_LABELS[detectedZone]})` : ''}</SelectItem>
                      <SelectItem value="nord">Solo Nord Italia</SelectItem>
                      <SelectItem value="centro">Solo Centro Italia</SelectItem>
                      <SelectItem value="sud">Solo Sud Italia e Isole</SelectItem>
                      <SelectItem value="all">Tutte le zone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="p-4 border-b flex flex-col gap-3">
                <div className="flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
                  <Input placeholder="Cerca marca, modello, descrizione..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" data-testid="listino-search" />
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-full md:w-[220px]" data-testid="listino-filter-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le categorie</SelectItem>
                      {(options.product_categories || []).map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterBrand} onValueChange={setFilterBrand}>
                    <SelectTrigger className="w-full md:w-[180px]" data-testid="listino-filter-brand"><SelectValue placeholder="Tutte le marche" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte le marche</SelectItem>
                      {brandOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {hasPhaseFilter && (
                    <div className="inline-flex rounded-lg border p-0.5 bg-secondary/30 h-9" data-testid="listino-filter-phase">
                      {[
                        { key: 'all', label: 'Tutte' },
                        { key: 'monofase', label: 'Monofase' },
                        { key: 'trifase', label: 'Trifase' },
                      ].map(opt => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setFilterPhase(opt.key)}
                          className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${filterPhase === opt.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                          data-testid={`listino-filter-phase-${opt.key}`}
                        >{opt.label}</button>
                      ))}
                    </div>
                  )}
                  <div className="flex-1" />
                  <div className="text-xs text-muted-foreground">{filtered.length} prodotti · pag. {currentPage}/{totalPages}</div>
                </div>
                <div className="flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground shrink-0">kWp min</Label>
                    <Input type="number" step="0.1" value={powerMin} onChange={(e) => setPowerMin(e.target.value)} placeholder="es. 3" className="w-20" data-testid="listino-filter-power-min" />
                    <Label className="text-xs text-muted-foreground shrink-0">max</Label>
                    <Input type="number" step="0.1" value={powerMax} onChange={(e) => setPowerMax(e.target.value)} placeholder="es. 10" className="w-20" data-testid="listino-filter-power-max" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground shrink-0">kWh min</Label>
                    <Input type="number" step="0.1" value={kwhMin} onChange={(e) => setKwhMin(e.target.value)} placeholder="es. 5" className="w-20" data-testid="listino-filter-kwh-min" />
                    <Label className="text-xs text-muted-foreground shrink-0">max</Label>
                    <Input type="number" step="0.1" value={kwhMax} onChange={(e) => setKwhMax(e.target.value)} placeholder="es. 15" className="w-20" data-testid="listino-filter-kwh-max" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground shrink-0">€ min</Label>
                    <Input type="number" step="10" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="es. 500" className="w-24" data-testid="listino-filter-price-min" />
                    <Label className="text-xs text-muted-foreground shrink-0">max</Label>
                    <Input type="number" step="10" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="es. 5000" className="w-24" data-testid="listino-filter-price-max" />
                  </div>
                  <div className="flex-1" />
                  {activeFiltersCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="listino-filters-reset">
                      <XIcon className="size-3.5 mr-1" />Reset filtri ({activeFiltersCount})
                    </Button>
                  )}
                </div>
              </div>
              {filtered.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><Package className="size-5 text-muted-foreground" /></div>
                  <div className="text-sm font-medium">Nessun prodotto a listino</div>
                </div>
              ) : (
                <>
                <Table data-testid="listino-table">
                  <TableHeader><TableRow><TableHead>Categoria</TableHead><TableHead>Marca / Modello</TableHead><TableHead>Descrizione</TableHead><TableHead className="text-right">Prezzo</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {pagedProducts.map(p => (
                      <TableRow key={p.id} data-testid={`listino-row-${p.id}`}>
                        <TableCell><Badge variant="secondary" className="text-xs">{catMap[p.category] || p.category}</Badge></TableCell>
                        <TableCell>
                          <div className="font-medium flex items-center gap-1.5 flex-wrap">{p.brand} {p.model}
                            {p.is_custom && <Badge variant="outline" className="gap-1 text-[10px] border-accent text-accent"><Sparkles className="size-3" />Personalizzabile</Badge>}
                            {p.phase_type && <Badge variant="outline" className="text-[10px] uppercase">{p.phase_type}</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">{p.power}{p.battery_kwh ? ` · Accumulo ${p.battery_kwh} kWh` : ''}</div>
                        </TableCell>
                        <TableCell className="max-w-md truncate text-sm">{p.description}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{canViewPrices ? formatCurrency(p.unit_price) : <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell><Button size="sm" variant="outline" disabled={!hasMinCustomerData} onClick={() => addToCart(p)} data-testid={`listino-add-${p.id}`} title={hasMinCustomerData ? 'Aggiungi al preventivo' : 'Compila prima i dati minimi del cliente'}><Plus className="size-4 mr-1" />Aggiungi</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {totalPages > 1 && (
                  <div className="p-3 border-t flex items-center justify-between gap-2 flex-wrap" data-testid="listino-pagination">
                    <div className="text-xs text-muted-foreground">
                      Mostrando {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filtered.length)} di {filtered.length}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage(1)} data-testid="listino-page-first">«</Button>
                      <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="listino-page-prev">‹ Precedente</Button>
                      <span className="px-3 text-sm tabular-nums" data-testid="listino-page-current">Pag. {currentPage} / {totalPages}</span>
                      <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="listino-page-next">Successiva ›</Button>
                      <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)} data-testid="listino-page-last">»</Button>
                    </div>
                  </div>
                )}
                </>
              )}
            </CardContent>
          </Card>

          <Card className="order-1" data-testid="listino-customer-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="size-4 text-primary" />1. Dati Cliente</CardTitle>
              <p className="text-xs text-muted-foreground">Compila i <strong>dati minimi obbligatori</strong> (Nome, Cognome, Città, Provincia) prima di aggiungere i prodotti al preventivo.</p>
            </CardHeader>
            <CardContent>
              {/* Tipo cliente: Privato / Azienda */}
              <div className="mb-4">
                <Label className="text-xs mb-2 block">Tipo cliente</Label>
                <div className="inline-flex rounded-lg border p-0.5 bg-secondary/50">
                  <button
                    type="button"
                    onClick={() => setCustomer({ ...customer, customer_type: 'privato' })}
                    className={`px-4 h-8 rounded-md text-xs font-medium transition-colors ${customer.customer_type === 'privato' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="listino-customer-type-privato"
                  >Privato</button>
                  <button
                    type="button"
                    onClick={() => setCustomer({ ...customer, customer_type: 'azienda' })}
                    className={`px-4 h-8 rounded-md text-xs font-medium transition-colors ${customer.customer_type === 'azienda' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="listino-customer-type-azienda"
                  >Azienda</button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {customer.customer_type === 'privato' ? (
                  <>
                    <div className="space-y-1.5"><Label className="text-xs">Nome <span className="text-destructive">*</span></Label><Input value={customer.first_name} onChange={(e) => setCustomer({ ...customer, first_name: e.target.value })} data-testid="listino-customer-firstname" className={!customer.first_name?.trim() ? 'border-destructive/40' : ''} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Cognome <span className="text-destructive">*</span></Label><Input value={customer.last_name} onChange={(e) => setCustomer({ ...customer, last_name: e.target.value })} data-testid="listino-customer-lastname" className={!customer.last_name?.trim() ? 'border-destructive/40' : ''} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Codice Fiscale</Label><Input value={customer.fiscal_code} onChange={(e) => setCustomer({ ...customer, fiscal_code: e.target.value.toUpperCase() })} className="font-mono" /></div>
                  </>
                ) : (
                  <>
                    <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Ragione sociale <span className="text-destructive">*</span></Label><Input value={customer.company_name} onChange={(e) => setCustomer({ ...customer, company_name: e.target.value })} data-testid="listino-customer-company" className={!customer.company_name?.trim() ? 'border-destructive/40' : ''} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Partita IVA</Label><Input value={customer.vat_number} onChange={(e) => setCustomer({ ...customer, vat_number: e.target.value })} className="font-mono" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Codice Fiscale</Label><Input value={customer.fiscal_code} onChange={(e) => setCustomer({ ...customer, fiscal_code: e.target.value.toUpperCase() })} className="font-mono" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Referente / Rapp. legale</Label><Input value={customer.first_name} onChange={(e) => setCustomer({ ...customer, first_name: e.target.value })} placeholder="Nome referente" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Cognome referente</Label><Input value={customer.last_name} onChange={(e) => setCustomer({ ...customer, last_name: e.target.value })} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">PEC</Label><Input type="email" value={customer.pec || ''} onChange={(e) => setCustomer({ ...customer, pec: e.target.value })} placeholder="pec@azienda.it" data-testid="listino-customer-pec" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Codice destinatario SDI</Label><Input value={customer.sdi_code || ''} onChange={(e) => setCustomer({ ...customer, sdi_code: e.target.value.toUpperCase() })} maxLength={7} className="font-mono" placeholder="XXXXXXX" data-testid="listino-customer-sdi" /></div>
                  </>
                )}
                <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Telefono</Label><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} /></div>
                <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Indirizzo{customer.customer_type === 'azienda' ? ' sede legale' : ''}</Label><Input value={customer.residence_address} onChange={(e) => setCustomer({ ...customer, residence_address: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Città <span className="text-destructive">*</span></Label><Input value={customer.residence_city} onChange={(e) => setCustomer({ ...customer, residence_city: e.target.value })} data-testid="listino-customer-city" className={!customer.residence_city?.trim() ? 'border-destructive/40' : ''} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label className="text-xs">CAP</Label><Input value={customer.residence_cap} onChange={(e) => setCustomer({ ...customer, residence_cap: e.target.value })} className="font-mono" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Prov. <span className="text-destructive">*</span></Label><Input value={customer.residence_province} onChange={(e) => setCustomer({ ...customer, residence_province: e.target.value.toUpperCase() })} maxLength={2} className={`font-mono uppercase ${!customer.residence_province?.trim() ? 'border-destructive/40' : ''}`} data-testid="listino-customer-province" /></div>
                </div>
              </div>
              <Separator className="my-4" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Validità (giorni)</Label><Input type="number" min="1" value={validityDays} onChange={(e) => setValidityDays(Number(e.target.value))} data-testid="listino-validity" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Aliquota IVA</Label>
                  <Select value={String(vatRate)} onValueChange={(v) => setVatRate(Number(v))}>
                    <SelectTrigger data-testid="listino-vat"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="10">10% (agevolata)</SelectItem><SelectItem value="22">22% (ordinaria)</SelectItem><SelectItem value="4">4%</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5 mt-3"><Label className="text-xs">Note libere</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Eventuali note per il cliente..." /></div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="lg:sticky lg:top-20" data-testid="listino-cart">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><ShoppingCart className="size-4" />Preventivo ({cart.length})</CardTitle>
              <Button variant="outline" size="sm" onClick={addFreeItem} disabled={!hasMinCustomerData} className="h-7 text-xs gap-1" data-testid="listino-add-free-item" title={hasMinCustomerData ? 'Aggiungi voce libera' : 'Compila prima i dati minimi del cliente'}>
                <Sparkles className="size-3" />Voce libera
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {cart.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-lg">Aggiungi prodotti dal listino o<br/>usa una <strong>voce libera</strong> personalizzata</div>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto">
                  {cart.map((it, i) => (
                    <div key={i} className={`border rounded-lg p-2.5 text-sm ${it.is_custom ? 'border-accent/50 bg-accent/5' : ''}`} data-testid={`listino-cart-item-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          {it.is_custom ? (
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-1.5">
                                <Sparkles className="size-3 text-accent" />
                                <span className="text-[10px] uppercase tracking-wider text-accent font-semibold">Voce personalizzabile</span>
                              </div>
                              <Input
                                value={it.model || ''}
                                onChange={(e) => updateCartItem(i, 'model', e.target.value)}
                                placeholder="Denominazione (es. Manodopera aggiuntiva)"
                                className="h-8 text-sm font-medium"
                                data-testid={`listino-cart-model-${i}`}
                              />
                              <Input
                                value={it.description || ''}
                                onChange={(e) => updateCartItem(i, 'description', e.target.value)}
                                placeholder="Descrizione dettagliata..."
                                className="h-8 text-xs"
                                data-testid={`listino-cart-desc-${i}`}
                              />
                            </div>
                          ) : (
                            <>
                              <div className="font-medium truncate">{it.brand} {it.model}</div>
                              <div className="text-xs text-muted-foreground truncate">{it.description}</div>
                            </>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" className="size-7 shrink-0" onClick={() => removeFromCart(i)}><Trash2 className="size-3.5 text-destructive" /></Button>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-2">
                        <div className="flex items-center gap-1">
                          <Input type="number" min="0" step="1" value={it.quantity} onChange={(e) => updateCartItem(i, 'quantity', Number(e.target.value))} className="h-7 w-16 text-xs tabular-nums" />
                          {canViewPrices && (
                            <>
                              <span className="text-xs text-muted-foreground">×</span>
                              <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updateCartItem(i, 'unit_price', Number(e.target.value))} className="h-7 w-24 text-xs tabular-nums" data-testid={`listino-cart-price-${i}`} />
                            </>
                          )}
                        </div>
                        {canViewPrices && <div className="text-sm font-medium tabular-nums">{formatCurrency((it.quantity || 0) * (it.unit_price || 0))}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Separator />
              {canViewPrices ? (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Imponibile</span><span className="tabular-nums">{formatCurrency(totals.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">IVA {vatRate}%</span><span className="tabular-nums">{formatCurrency(totals.vat)}</span></div>
                  <Separator />
                  <div className="flex justify-between text-base font-semibold"><span>Totale</span><span className="tabular-nums" data-testid="listino-total">{formatCurrency(totals.total)}</span></div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground bg-secondary/50 border rounded-md px-3 py-2" data-testid="prices-hidden-note">
                  Prezzi nascosti dal responsabile della tua azienda. Il PDF verrà comunque generato con i prezzi di listino corretti.
                </div>
              )}
              <Button className="w-full" onClick={handleGenerate} disabled={generating || cart.length === 0} data-testid="listino-generate-button"><Sparkles className="size-4 mr-2" />Genera PDF preventivo</Button>
              <Button className="w-full" variant="outline" onClick={() => setCart([])} disabled={cart.length === 0}>Svuota preventivo</Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <QuoteBusinessPlanDialog
        open={bpDialogOpen}
        onOpenChange={setBpDialogOpen}
        payload={buildPayload()}
      />
    </div>
  );
}
